import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  changePassword,
  createRechargeOrder,
  createToken,
  fetchApiKey,
  fetchRechargeOptions,
  fetchUsage,
  listTokens,
  revokeApiKey,
  revokeToken,
  updateProfile,
} from "@/lib/api";
import { formatMoney, formatQuota, deriveBaseURLs } from "@/lib/format";
import type {
  ApiKey,
  InstallToken,
  RechargeOption,
  RechargeOrder,
  UsageDaily,
  UsageResponse,
  UserPublic,
} from "@/lib/types";
import { cn } from "@/lib/utils";
import { CopyIcon } from "./CopyIcon";
import type { ConsoleTab } from "./Sidebar";

// 控制台应用市场 —— 与官网 AgentMarket 保持同一套货架信息。
// canInstall = 服务端目前支持创建对应 token（agent.ByName + RequiresModelConfig）。
type ConsoleAgent = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  highlights: string[];
  version?: string;
  cover: string;
  canInstall: boolean;
};

const CONSOLE_AGENTS: ConsoleAgent[] = [
  {
    id: "codex",
    name: "Codex",
    tagline: "OpenAI Codex CLI",
    description:
      "OpenAI 出品的命令行 coding agent。Agentfly 安装包预置模型通道、代理与配置文件，复制命令直接跑。环境变量风格沿用 OpenAI 兼容。",
    highlights: ["环境自动", "通道已接通", "三平台覆盖", "国内代理已配"],
    version: "0.130.0",
    cover: "/agents/codex.png",
    canInstall: true,
  },
  {
    id: "claude",
    name: "Claude Code",
    tagline: "Anthropic 官方 CLI",
    description:
      "Anthropic 出品的 Claude Code。Agentfly 版本附独立 Node 运行时与模型通道，安装即可直接 claude --version 启动。环境变量风格沿用 Anthropic 兼容。",
    highlights: ["官方一致", "通道已接通", "三平台覆盖", "独立 Node 运行时"],
    version: "2.1.141",
    cover: "/agents/claude.png",
    canInstall: true,
  },
  {
    id: "hermes",
    name: "Hermes",
    tagline: "智能体研究框架",
    description:
      "自带完整 Python 与 Node 运行时，离线可用。当前仅通过一键安装交付，无需环境变量。",
    highlights: ["内置 Python + Node 运行时", "完全离线可跑", "目录隔离"],
    version: "v2026.5.7",
    cover: "/agents/hermes.png",
    canInstall: true,
  },
  {
    id: "openclaw",
    name: "openclaw",
    tagline: "开源命令行编程助手",
    description: "国内源直连，插件即装即用，飞书通道开箱可用。当前仅通过一键安装交付。",
    highlights: ["国内 npm 源", "飞书通道", "插件即装即用"],
    version: "2026.5.7",
    cover: "/agents/openclaw.png",
    canInstall: true,
  },
];

// 这些 agent 的一键安装命令是 Linux/macOS 专用，Windows 用户需要先装 WSL。
const NEEDS_WSL_BOOTSTRAP = new Set(["hermes", "openclaw"]);

const DONUT_PALETTE = [
  "#3b82f6", "#10b981", "#a855f7", "#f59e0b", "#ec4899",
  "#0ea5e9", "#84cc16", "#ef4444", "#6366f1", "#14b8a6",
];

interface PanelsProps {
  active: ConsoleTab;
  session: string;
  user: UserPublic;
  apiKey: ApiKey | null;
  onApiKeyUpdate: (next: ApiKey | null) => void;
  onUserUpdate: (next: UserPublic) => void;
  onTabChange: (tab: ConsoleTab) => void;
}

export function Panels(props: PanelsProps) {
  return (
    <>
      <MarketPanel {...props} active={props.active === "market"} />
      <UsagePanel {...props} active={props.active === "usage"} />
      <AccountPanel {...props} active={props.active === "account"} />
      <RechargePanel {...props} active={props.active === "recharge"} />
    </>
  );
}

interface PanelChildProps extends Omit<PanelsProps, "active"> {
  active: boolean;
}

/* ============================================================
   用量统计 (UsagePanel)
   ============================================================ */

type UsageMode = "today" | "day" | "range";
type TrendMetric = "cost" | "request" | "tokens" | "search" | "image";
type ModelSort = "cost" | "request" | "tokens";

function formatYMD(d: Date): string {
  const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function computeWindow(mode: UsageMode, day: string, start: string, end: string) {
  const now = new Date();
  if (mode === "today") {
    const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return {
      start: Math.floor(dayStart.getTime() / 1000),
      end: Math.floor(now.getTime() / 1000),
      bucket: "hour" as const,
    };
  }
  if (mode === "day" && day) {
    const d = new Date(day + "T00:00:00");
    const next = new Date(d.getTime() + 24 * 60 * 60 * 1000);
    return {
      start: Math.floor(d.getTime() / 1000),
      end: Math.floor(next.getTime() / 1000) - 1,
      bucket: "hour" as const,
    };
  }
  const startDate = start ? new Date(start + "T00:00:00") : new Date(Date.now() - 6 * 86400000);
  const endDate = end ? new Date(end + "T23:59:59") : now;
  return {
    start: Math.floor(startDate.getTime() / 1000),
    end: Math.floor(endDate.getTime() / 1000),
    bucket: "day" as const,
  };
}

function UsagePanel({ active, session }: PanelChildProps) {
  const today = useMemo(() => formatYMD(new Date()), []);
  const weekAgo = useMemo(() => formatYMD(new Date(Date.now() - 6 * 86400000)), []);
  const [mode, setMode] = useState<UsageMode>("range");
  const [day, setDay] = useState(today);
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [trendMetric, setTrendMetric] = useState<TrendMetric>("cost");
  const [modelSort, setModelSort] = useState<ModelSort>("cost");
  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!session) return;
    const win = computeWindow(mode, day, startDate, endDate);
    setLoading(true);
    setError("");
    try {
      const resp = await fetchUsage(session, win);
      setUsage(resp);
    } catch (err) {
      setError(err instanceof Error ? err.message : "查询失败");
    } finally {
      setLoading(false);
    }
  }, [session, mode, day, startDate, endDate]);

  useEffect(() => {
    if (!active) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    void refresh();
  }, [active, refresh]);

  // Refresh whenever the mode changes (today vs day vs range), but
  // only when the panel is active so we don't waste a request on
  // first load for users who never visit usage.
  useEffect(() => {
    if (!active) return;
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const summary = usage?.summary;
  const models = usage?.models ?? [];
  const daily = usage?.daily ?? [];

  const sortedModels = useMemo(() => {
    const arr = [...models];
    if (modelSort === "request") {
      arr.sort((a, b) => (b.request_count || 0) - (a.request_count || 0));
    } else if (modelSort === "tokens") {
      arr.sort((a, b) =>
        (b.prompt_tokens + b.completion_tokens) - (a.prompt_tokens + a.completion_tokens)
      );
    }
    return arr; // backend already sorted by quota for "cost"
  }, [models, modelSort]);

  return (
    <section className={cn("c2-panel", active && "active")}>
      <article className="c2-card c2-usage-controls">
        <div className="c2-usage-tabs" role="tablist">
          <UsageTab id="today" label="今日实时" mode={mode} onClick={setMode}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
          </UsageTab>
          <UsageTab id="day" label="指定日期" mode={mode} onClick={setMode}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4" /><path d="M16 3v4" /><path d="M3 11h18" /></svg>
          </UsageTab>
          <UsageTab id="range" label="时间范围" mode={mode} onClick={setMode}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 6h16" /><path d="M4 12h16" /><path d="M4 18h10" /></svg>
          </UsageTab>
          <button
            type="button"
            className="c2-btn c2-btn-primary c2-btn-sm c2-usage-query"
            onClick={refresh}
            disabled={loading}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
            <span>{loading ? "查询中…" : "查询"}</span>
          </button>
          <button
            type="button"
            className="c2-icon-btn"
            aria-label="刷新"
            onClick={refresh}
            disabled={loading}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 4v4h-4" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 20v-4h4" /></svg>
          </button>
        </div>

        {mode === "range" ? (
          <div className="c2-usage-fields">
            <label className="c2-field">
              <span>开始日期</span>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="c2-field">
              <span>结束日期</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
        ) : null}
        {mode === "day" ? (
          <div className="c2-usage-fields">
            <label className="c2-field">
              <span>日期</span>
              <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
            </label>
          </div>
        ) : null}

        <p className="c2-usage-hint" style={error ? { color: "#ef4444" } : undefined}>
          {error || "最多可查询 31 天内的数据"}
        </p>
      </article>

      <div className="c2-stats c2-stats-4">
        <Stat icon="blue"   label="总花费"    value={"￥" + formatMoney(summary?.total_cost_cny || 0)} />
        <Stat icon="green"  label="总请求"    value={formatQuota(summary?.total_requests || 0)} />
        <Stat icon="cyan"   label="网络搜索"  value={formatQuota(summary?.search_count || 0)} />
        <Stat icon="pink"   label="图片处理"  value={formatQuota(summary?.image_count || 0)} />
      </div>
      <div className="c2-stats c2-stats-4">
        <Stat icon="purple" label="总 Tokens"   value={formatQuota(summary?.total_tokens || 0)} />
        <Stat icon="violet" label="输入 Tokens" value={formatQuota(summary?.total_prompt_tokens || 0)} />
        <Stat icon="amber"  label="输出 Tokens" value={formatQuota(summary?.total_completion_tokens || 0)} />
        <Stat icon="indigo" label="缓存读写"    value={formatQuota(summary?.cached_tokens || 0)} />
      </div>

      <div className="c2-grid c2-grid-2">
        <DonutCard title="模型费用分布" models={models} kind="cost" />
        <DonutCard title="模型调用分布" models={models} kind="request" />
      </div>

      <article className="c2-card">
        <header className="c2-card-head">
          <div><h3>模型服务使用详情</h3></div>
          <select
            className="c2-select c2-select-sm"
            value={modelSort}
            onChange={(e) => setModelSort(e.target.value as ModelSort)}
          >
            <option value="cost">按花费排序</option>
            <option value="request">按请求数排序</option>
            <option value="tokens">按 Tokens 排序</option>
          </select>
        </header>
        <div className="c2-table-wrap">
          <table className="c2-table">
            <thead>
              <tr>
                <th>模型</th><th>请求数</th><th>网络搜索</th>
                <th>输入 Tokens</th><th>输出 Tokens</th><th>缓存读写</th>
                <th>图片数</th><th>花费</th><th>占比</th>
              </tr>
            </thead>
            <tbody>
              {sortedModels.length === 0 ? (
                <tr><td colSpan={9} className="c2-table-empty">暂无数据</td></tr>
              ) : sortedModels.map((m) => (
                <tr key={m.model}>
                  <td>{m.model}</td>
                  <td>{formatQuota(m.request_count)}</td>
                  <td>{formatQuota(m.search_count)}</td>
                  <td>{formatQuota(m.prompt_tokens)}</td>
                  <td>{formatQuota(m.completion_tokens)}</td>
                  <td>{formatQuota(m.cached_tokens)}</td>
                  <td>{formatQuota(m.image_count)}</td>
                  <td>￥{formatMoney(m.cost_cny)}</td>
                  <td>{((m.ratio_cost || 0) * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>

      <article className="c2-card">
        <header className="c2-card-head"><div><h3>每日趋势统计</h3></div></header>
        <div className="c2-trend-tabs">
          {(["cost", "request", "tokens", "search", "image"] as TrendMetric[]).map((m) => (
            <button
              key={m}
              type="button"
              className={cn("c2-trend-tab", trendMetric === m && "active")}
              onClick={() => setTrendMetric(m)}
            >
              {m === "cost" ? "花费" : m === "request" ? "请求" : m === "tokens" ? "Tokens" : m === "search" ? "搜索" : "图片"}
            </button>
          ))}
        </div>
        <div className="c2-chart-wrap c2-chart-wrap-line">
          <LineChart daily={daily} metric={trendMetric} />
        </div>
        <div className="c2-table-wrap">
          <table className="c2-table">
            <thead>
              <tr><th>日期</th><th>请求</th><th>花费</th><th>Tokens</th><th>图片</th><th>主要模型</th></tr>
            </thead>
            <tbody>
              {daily.length === 0 ? (
                <tr><td colSpan={6} className="c2-table-empty">暂无数据</td></tr>
              ) : daily.map((d) => (
                <tr key={d.date}>
                  <td>{d.date}</td>
                  <td>{formatQuota(d.request_count)}</td>
                  <td><strong>￥{formatMoney(d.cost_cny)}</strong></td>
                  <td>{formatQuota(d.tokens)}</td>
                  <td>{formatQuota(d.image_count)}</td>
                  <td>{d.top_model || "--"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </section>
  );
}

function UsageTab({
  id, label, mode, onClick, children,
}: {
  id: UsageMode;
  label: string;
  mode: UsageMode;
  onClick: (mode: UsageMode) => void;
  children: React.ReactNode;
}) {
  const active = id === mode;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={cn("c2-usage-tab", active && "active")}
      onClick={() => onClick(id)}
    >
      {children}
      <span>{label}</span>
    </button>
  );
}

interface StatProps {
  icon: "blue" | "amber" | "green" | "violet" | "cyan" | "pink" | "purple" | "indigo";
  label: string;
  value: string;
}

function Stat({ icon, label, value }: StatProps) {
  return (
    <div className="c2-stat">
      <div className={`c2-stat-icon c2-icon-${icon}`} aria-hidden="true">
        <span style={{ fontWeight: 600 }}>·</span>
      </div>
      <div className="c2-stat-body">
        <span className="c2-stat-label">{label}</span>
        <strong className="c2-stat-value">{value}</strong>
      </div>
    </div>
  );
}

function DonutCard({
  title, models, kind,
}: {
  title: string;
  models: UsageResponse["models"];
  kind: "cost" | "request";
}) {
  const segs = models
    .map((m, idx) => ({
      label: m.model,
      value: kind === "cost" ? m.cost_cny : m.request_count,
      color: DONUT_PALETTE[idx % DONUT_PALETTE.length],
    }))
    .filter((s) => s.value > 0);
  const total = segs.reduce((a, b) => a + b.value, 0);

  return (
    <article className="c2-card">
      <header className="c2-card-head"><div><h3>{title}</h3></div></header>
      <div className="c2-chart-wrap">
        <svg className="c2-donut" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <circle cx={100} cy={100} r={70} fill="none" stroke="rgba(15,23,42,0.06)" strokeWidth={22} />
          {(() => {
            if (total <= 0) return null;
            const radius = 70;
            const circumference = 2 * Math.PI * radius;
            let offset = 0;
            return segs.map((s) => {
              const seglen = (s.value / total) * circumference;
              const rest = circumference - seglen;
              const node = (
                <circle
                  key={s.label}
                  cx={100} cy={100} r={radius} fill="none"
                  stroke={s.color}
                  strokeWidth={22}
                  strokeDasharray={`${seglen.toFixed(2)} ${rest.toFixed(2)}`}
                  strokeDashoffset={(-offset).toFixed(2)}
                  transform="rotate(-90 100 100)"
                />
              );
              offset += seglen;
              return node;
            });
          })()}
        </svg>
        <ul className="c2-legend">
          {segs.length === 0 ? (
            <li><span style={{ color: "var(--c2-muted, #94a3b8)" }}>暂无数据</span></li>
          ) : segs.map((s) => (
            <li key={s.label}>
              <i style={{ background: s.color }} />
              <span>{s.label}: {((s.value / total) * 100).toFixed(1)}%</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
}

function LineChart({ daily, metric }: { daily: UsageDaily[]; metric: TrendMetric }) {
  const series = [...daily].reverse(); // backend daily is newest-first; chart wants oldest-first
  const width = 800;
  const height = 280;
  const padTop = 24;
  const padBottom = 36;
  const padLeft = 64;
  const padRight = 24;
  const innerW = width - padLeft - padRight;
  const innerH = height - padTop - padBottom;

  if (!series.length) {
    return (
      <svg className="c2-line" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <text x={width / 2} y={height / 2} textAnchor="middle" fill="#94a3b8" fontSize={13}>暂无数据</text>
      </svg>
    );
  }

  const values = series.map((d) => {
    switch (metric) {
      case "request": return d.request_count;
      case "tokens":  return d.tokens;
      case "search":  return d.search_count;
      case "image":   return d.image_count;
      default:        return d.cost_cny;
    }
  });
  const maxV = Math.max(...values, 0) || 1;
  const ticks = 5;
  const stepX = series.length > 1 ? innerW / (series.length - 1) : 0;
  const points = series.map((_, idx) => {
    const x = padLeft + (series.length === 1 ? innerW / 2 : stepX * idx);
    const y = padTop + innerH * (1 - values[idx] / maxV);
    return [x, y] as const;
  });
  const fillPath =
    `M ${points[0][0]} ${padTop + innerH} ` +
    `L ${points.map((p) => p.join(" ")).join(" L ")} ` +
    `L ${points[points.length - 1][0]} ${padTop + innerH} Z`;
  const linePath = `M ${points.map((p) => p.join(" ")).join(" L ")}`;

  return (
    <svg className="c2-line" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {Array.from({ length: ticks + 1 }).map((_, i) => {
        const y = padTop + innerH * (i / ticks);
        const tickV = maxV * (1 - i / ticks);
        const label = metric === "cost" ? "￥" + tickV.toFixed(2) : formatQuota(Math.round(tickV));
        return (
          <g key={i}>
            <line className={i === ticks ? "axis" : "grid-line"} x1={padLeft} x2={width - padRight} y1={y} y2={y} />
            <text className="axis-label" x={padLeft - 8} y={y + 4} textAnchor="end">{label}</text>
          </g>
        );
      })}
      <path className="data-fill" d={fillPath} />
      <path className="data-line" d={linePath} />
      {points.map((p, idx) => {
        const dt = new Date(series[idx].date + "T00:00:00");
        const xLabel = `${dt.getMonth() + 1}/${dt.getDate()}`;
        return (
          <g key={idx}>
            <circle className="data-point" cx={p[0]} cy={p[1]} r={3.5} />
            <text className="axis-label" x={p[0]} y={height - padBottom + 18} textAnchor="middle">{xLabel}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ============================================================
   我的账户 (AccountPanel)
   ============================================================ */

function AccountPanel({ active, session, user, apiKey, onApiKeyUpdate, onUserUpdate }: PanelChildProps) {
  const [keyVisible, setKeyVisible] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRevoke, setShowRevoke] = useState(false);
  const [actionMsg, setActionMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Lazy-fetch the key when entering this panel — the dashboard mount
  // path in ConsolePage only fetches once, so a panel revisit should
  // pick up any revoke/remint that happened elsewhere.
  useEffect(() => {
    if (!active || apiKey || !session) return;
    void fetchApiKey(session)
      .then((resp) => onApiKeyUpdate(resp.api_key))
      .catch(() => {});
  }, [active, apiKey, session, onApiKeyUpdate]);

  const handleRevoke = useCallback(async () => {
    setBusy(true);
    try {
      const resp = await revokeApiKey(session);
      onApiKeyUpdate(resp.api_key);
      setKeyVisible(true);
      setActionMsg({ kind: "ok", text: "已重新生成新 Key" });
      setTimeout(() => setShowRevoke(false), 700);
    } catch (err) {
      setActionMsg({ kind: "error", text: err instanceof Error ? err.message : "撤销失败" });
    } finally {
      setBusy(false);
    }
  }, [session, onApiKeyUpdate]);

  const handleProfileSubmit = useCallback(async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const data = new FormData(ev.currentTarget);
    setBusy(true);
    try {
      const resp = await updateProfile(session, {
        username: String(data.get("username") || "").trim().toLowerCase() || undefined,
        display_name: String(data.get("display_name") || "").trim() || undefined,
        email: String(data.get("email") || "").trim() || undefined,
      });
      onUserUpdate(resp.user);
      setShowProfile(false);
      setActionMsg({ kind: "ok", text: "资料已保存" });
    } catch (err) {
      setActionMsg({ kind: "error", text: err instanceof Error ? err.message : "保存失败" });
    } finally {
      setBusy(false);
    }
  }, [session, onUserUpdate]);

  const handlePasswordSubmit = useCallback(async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const data = new FormData(ev.currentTarget);
    const oldP = String(data.get("old_password") || "");
    const newP = String(data.get("new_password") || "");
    if (newP.length < 8) {
      setActionMsg({ kind: "error", text: "新密码至少 8 位" });
      return;
    }
    setBusy(true);
    try {
      await changePassword(session, oldP, newP);
      setShowPassword(false);
      setActionMsg({ kind: "ok", text: "密码已更新" });
    } catch (err) {
      setActionMsg({ kind: "error", text: err instanceof Error ? err.message : "更新失败" });
    } finally {
      setBusy(false);
    }
  }, [session]);

  const baseURLs = deriveBaseURLs(apiKey?.base_url || "https://apis.agentfly.net/v1");
  const displayKey = apiKey?.key || "";
  const masked = apiKey?.masked || "尚未生成";

  return (
    <section className={cn("c2-panel", active && "active")}>
      <article className="c2-card">
        <header className="c2-card-head">
          <div>
            <span className="c2-eyebrow">账户</span>
            <h3>账户信息</h3>
          </div>
          <div className="c2-card-actions">
            <button className="c2-btn c2-btn-ghost c2-btn-sm" type="button" onClick={() => setShowProfile(true)}>编辑资料</button>
            <button className="c2-btn c2-btn-ghost c2-btn-sm" type="button" onClick={() => setShowPassword(true)}>修改密码</button>
          </div>
        </header>
        <dl className="c2-kv c2-kv-rows">
          <div><dt>用户名</dt><dd>{user.username}</dd></div>
          <div><dt>显示名</dt><dd>{user.display_name || "--"}</dd></div>
          <div><dt>邮箱</dt><dd>{user.email || "--"}</dd></div>
          <div><dt>余额</dt><dd><strong>￥{formatMoney(user.balance_cny || 0)}</strong></dd></div>
        </dl>
      </article>

      <article className="c2-card">
        <header className="c2-card-head">
          <div>
            <span className="c2-eyebrow">访问凭证</span>
            <h3>当前 API Key</h3>
          </div>
        </header>
        <dl className="c2-kv c2-kv-rows">
          <div>
            <dt>API Key</dt>
            <dd className="c2-kv-copyable">
              <code className="c2-masked-key">{keyVisible ? (displayKey || masked) : masked}</code>
              <button
                type="button"
                className="c2-kv-toggle"
                onClick={() => setKeyVisible((v) => !v)}
                aria-pressed={keyVisible}
                aria-label={keyVisible ? "隐藏 Key" : "显示完整 Key"}
                title={keyVisible ? "隐藏" : "显示"}
              >
                {keyVisible ? "隐藏" : "显示"}
              </button>
              <CopyIcon
                text={displayKey || ""}
                ariaLabel="复制 API Key"
                className="c2-kv-copy"
              />
              <button
                type="button"
                className="c2-kv-toggle c2-kv-toggle-danger"
                onClick={() => setShowRevoke(true)}
                aria-label="撤销并重新生成"
                title="撤销并重新生成"
              >
                撤销并重新生成
              </button>
            </dd>
          </div>
          <div>
            <dt>Base URL · OpenAI 兼容</dt>
            <dd className="c2-kv-copyable">
              <span>{baseURLs.openai}</span>
              <CopyIcon text={baseURLs.openai} ariaLabel="复制 OpenAI Base URL" className="c2-kv-copy" />
            </dd>
          </div>
          <div>
            <dt>Base URL · Anthropic 兼容</dt>
            <dd className="c2-kv-copyable">
              <span>{baseURLs.anthropic}</span>
              <CopyIcon text={baseURLs.anthropic} ariaLabel="复制 Anthropic Base URL" className="c2-kv-copy" />
            </dd>
          </div>
          <div>
            <dt>创建时间</dt>
            <dd>{apiKey?.created_at ? new Date(apiKey.created_at).toLocaleString("zh-CN", { hour12: false }) : "--"}</dd>
          </div>
        </dl>
        <p className="c2-card-foot">
          codex / cursor 等 OpenAI 兼容客户端用 OpenAI Base URL；claude
          等 Anthropic SDK 客户端用 Anthropic Base URL（SDK 会自动追加
          <code> /v1/messages</code>）。每个用户仅持有一把 API Key，撤销后会立刻重新签发。
        </p>
      </article>

      {actionMsg ? (
        <p className={cn("c2-form-message", actionMsg.kind)} role="status">{actionMsg.text}</p>
      ) : null}

      <Modal open={showProfile} title="编辑资料" onClose={() => setShowProfile(false)}>
        <form className="c2-form" onSubmit={handleProfileSubmit}>
          <label className="c2-field">
            <span>用户名</span>
            <input name="username" type="text" autoComplete="username" defaultValue={user.username} placeholder="4-32 位 a-z, 0-9, _" />
          </label>
          <label className="c2-field">
            <span>显示名</span>
            <input name="display_name" type="text" autoComplete="nickname" defaultValue={user.display_name} />
          </label>
          <label className="c2-field">
            <span>邮箱</span>
            <input name="email" type="email" autoComplete="email" defaultValue={user.email} />
          </label>
          <div className="c2-modal-actions">
            <button className="c2-btn c2-btn-ghost" type="button" onClick={() => setShowProfile(false)}>取消</button>
            <button className="c2-btn c2-btn-primary" type="submit" disabled={busy}>保存</button>
          </div>
        </form>
      </Modal>

      <Modal open={showPassword} title="修改密码" onClose={() => setShowPassword(false)}>
        <form className="c2-form" onSubmit={handlePasswordSubmit}>
          <label className="c2-field">
            <span>当前密码</span>
            <input name="old_password" type="password" autoComplete="current-password" required />
          </label>
          <label className="c2-field">
            <span>新密码</span>
            <input name="new_password" type="password" autoComplete="new-password" required minLength={8} placeholder="至少 8 位" />
          </label>
          <div className="c2-modal-actions">
            <button className="c2-btn c2-btn-ghost" type="button" onClick={() => setShowPassword(false)}>取消</button>
            <button className="c2-btn c2-btn-primary" type="submit" disabled={busy}>更新密码</button>
          </div>
        </form>
      </Modal>

      <Modal open={showRevoke} title="撤销并重新生成 API Key" onClose={() => setShowRevoke(false)}>
        <p className="c2-modal-hint">
          撤销后旧 Key 立即失效，已粘贴到 codex / claude 等设备上的旧 Key 将无法继续使用，请确认无人在使用后再操作。
        </p>
        <div className="c2-modal-actions">
          <button className="c2-btn c2-btn-ghost" type="button" onClick={() => setShowRevoke(false)}>取消</button>
          <button className="c2-btn c2-btn-primary" type="button" onClick={handleRevoke} disabled={busy}>
            确认撤销并重新生成
          </button>
        </div>
      </Modal>
    </section>
  );
}

/* ============================================================
   应用市场 (MarketPanel) v2 - 卡片商店
   ============================================================ */

type MarketModalTab = "overview" | "install";

function MarketPanel({ active, session, apiKey, onApiKeyUpdate }: PanelChildProps) {
  const [tokens, setTokens] = useState<InstallToken[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [modalAgent, setModalAgent] = useState<ConsoleAgent | null>(null);
  const [modalTab, setModalTab] = useState<MarketModalTab>("overview");
  const fetchedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!session) return;
    setError("");
    try {
      const resp = await listTokens(session);
      setTokens(resp.tokens || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败");
    }
  }, [session]);

  useEffect(() => {
    if (!active) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    void refresh();
  }, [active, refresh]);

  // Esc 关 modal
  useEffect(() => {
    if (!modalAgent) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModalAgent(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalAgent]);

  const openModal = (agent: ConsoleAgent, tab: MarketModalTab) => {
    setModalTab(tab);
    setModalAgent(agent);
  };

  const handleCreate = useCallback(async (agentType?: string) => {
    const target = agentType || (modalAgent && modalAgent.canInstall ? modalAgent.id : "");
    if (!target) return;
    setBusy(true);
    setError("");
    try {
      const resp = await createToken(session, {
        agent_type: target,
        expires: "8760h",
      });
      if (resp.api_key && !apiKey) onApiKeyUpdate(resp.api_key);
      void refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "创建失败");
    } finally {
      setBusy(false);
    }
  }, [session, modalAgent, apiKey, onApiKeyUpdate, refresh]);

  const handleRevoke = useCallback(
    async (id: string) => {
      if (!window.confirm("撤销授权码 " + id + " 吗？撤销后无法恢复。")) return;
      try {
        await revokeToken(session, id);
        void refresh();
      } catch (err) {
        window.alert(err instanceof Error ? err.message : "撤销失败");
      }
    },
    [session, refresh],
  );

  return (
    <section className={cn("c2-panel c2-market-panel", active && "active")}>
      <article className="c2-card c2-market-cards-card">
        <header className="c2-card-head">
          <div>
            <span className="c2-eyebrow">智能体货架</span>
            <h3>挑一个，一键安装或一键接入</h3>
          </div>
          <div className="c2-card-actions">
            <button className="c2-btn c2-btn-ghost c2-btn-sm" type="button" onClick={refresh}>
              刷新
            </button>
          </div>
        </header>
        {error ? <p className="c2-form-message error">{error}</p> : null}

        <div className="c2-market-grid">
          {CONSOLE_AGENTS.map((a) => {
            const count = tokens.filter((t) => t.agent_type === a.id && !t.revoked).length;
            return (
              <article
                key={a.id}
                className="c2-market-card"
                onClick={() => openModal(a, "overview")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openModal(a, "overview");
                  }
                }}
                aria-label={`${a.name} —— ${a.tagline}，查看详情`}
              >
                <div className="c2-market-card-cover">
                  <img src={a.cover} alt="" loading="lazy" />
                  {count > 0 ? (
                    <span className="c2-market-card-count" aria-label={`${count} 个有效授权码`}>
                      {count}
                    </span>
                  ) : null}
                </div>
                <div className="c2-market-card-body">
                  <div className="c2-market-card-name">{a.name}</div>
                  <div className="c2-market-card-tag">{a.tagline}</div>
                  <div className="c2-market-card-pills">
                    {a.canInstall ? (
                      <span className="c2-market-card-pill">一键安装</span>
                    ) : (
                      <span className="c2-market-card-pill is-soft">一键安装</span>
                    )}
                  </div>
                </div>
                <div className="c2-market-card-foot">
                  <button
                    type="button"
                    className="c2-market-card-cta"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      openModal(a, "install");
                    }}
                  >
                    获取授权码
                    <span className="c2-market-card-cta-arrow" aria-hidden="true">→</span>
                  </button>
                  <span className="c2-market-card-foot-meta">版本 {a.version}</span>
                </div>
              </article>
            );
          })}
        </div>

        <p className="c2-card-foot c2-market-cards-hint">
          所有授权码都会复用「我的账户」里的当前 API Key。撤销并重置 API Key 后，请重新生成授权码与安装命令。
        </p>
      </article>

      {modalAgent ? (
        <MarketModal
          agent={modalAgent}
          tab={modalTab}
          setTab={setModalTab}
          tokens={tokens.filter((t) => t.agent_type === modalAgent.id)}
          wslTokens={tokens.filter((t) => t.agent_type === "wsl" && !t.revoked)}
          busy={busy}
          error={error}
          onClose={() => setModalAgent(null)}
          onCreate={handleCreate}
          onRevoke={handleRevoke}
        />
      ) : null}
    </section>
  );
}

interface MarketModalProps {
  agent: ConsoleAgent;
  tab: MarketModalTab;
  setTab: (t: MarketModalTab) => void;
  tokens: InstallToken[];
  wslTokens: InstallToken[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onCreate: (agentType?: string) => void;
  onRevoke: (id: string) => void;
}

function MarketModal({
  agent,
  tab,
  setTab,
  tokens,
  wslTokens,
  busy,
  error,
  onClose,
  onCreate,
  onRevoke,
}: MarketModalProps) {
  useEffect(() => {
    document.body.classList.add("market-modal-open");
    return () => document.body.classList.remove("market-modal-open");
  }, []);

  const tabs: Array<{ id: MarketModalTab; label: string }> = [
    { id: "overview", label: "概览" },
    { id: "install", label: "一键安装" },
  ];

  return (
    <div className="market-modal c2-market-modal" role="dialog" aria-modal="true" aria-label={`${agent.name} 详情`}>
      <div className="market-modal-backdrop" onClick={onClose} />
      <div
        className="market-modal-card market-modal-detail"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="market-modal-close" type="button" aria-label="关闭" onClick={onClose}>
          ×
        </button>

        <header className="market-detail-hero">
          <img src={agent.cover} alt="" />
          <div className="market-detail-hero-meta">
            <h3>{agent.name}</h3>
            <p>{agent.tagline}</p>
          </div>
        </header>

        <nav className="market-detail-tabs" role="tablist" aria-label="智能体详情">
          {tabs.map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              className={"market-detail-tab" + (tab === t.id ? " is-active" : "")}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="market-detail-body">
          {tab === "overview" && <MarketOverview agent={agent} />}
          {tab === "install" && (
            <MarketInstall
              agent={agent}
              tokens={tokens}
              wslTokens={wslTokens}
              busy={busy}
              error={error}
              onCreate={onCreate}
              onRevoke={onRevoke}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function MarketOverview({ agent }: { agent: ConsoleAgent }) {
  return (
    <div className="market-detail-section">
      <h4>{agent.tagline}</h4>
      <p>{agent.description}</p>
      <ul className="market-detail-highlights">
        {agent.highlights.map((h) => (
          <li key={h}>
            <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
              <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{h}</span>
          </li>
        ))}
      </ul>
      <dl className="market-detail-meta">
        <div>
          <dt>版本</dt>
          <dd><code>{agent.version}</code></dd>
        </div>
      </dl>
    </div>
  );
}

function MarketInstall({
  agent,
  tokens,
  wslTokens,
  busy,
  error,
  onCreate,
  onRevoke,
}: {
  agent: ConsoleAgent;
  tokens: InstallToken[];
  wslTokens: InstallToken[];
  busy: boolean;
  error: string;
  onCreate: (agentType?: string) => void;
  onRevoke: (id: string) => void;
}) {
  if (!agent.canInstall) {
    return (
      <div className="market-detail-section">
        <h4>{agent.name} 在控制台暂未开放自助创建授权码</h4>
        <p>
          可前往
          <a href="/#market" style={{ color: "var(--console-accent, #10b981)", margin: "0 .25rem", borderBottom: "1px solid currentColor" }}>
            官网应用市场
          </a>
          查看一键安装命令，或邮件联系 <code>hello@agentfly.net</code> 申请。
        </p>
      </div>
    );
  }
  return (
    <div className="market-detail-section">
      <div className="market-install-head">
        <h4>授权码 · 一键安装命令</h4>
        <button
          type="button"
          className="c2-btn c2-btn-primary c2-btn-sm"
          onClick={() => onCreate()}
          disabled={busy}
        >
          {busy ? "创建中…" : "+ 新建授权码"}
        </button>
      </div>
      {error ? <p className="c2-form-message error">{error}</p> : null}
      {tokens.length === 0 ? (
        <p className="market-install-empty">
          还没有 {agent.name} 的授权码，点右上角「+ 新建授权码」生成。
        </p>
      ) : (
        <ul className="market-install-list">
          {tokens.map((t) => (
            <InstallTokenCard key={t.token_id} token={t} onRevoke={onRevoke} />
          ))}
        </ul>
      )}
      {NEEDS_WSL_BOOTSTRAP.has(agent.id) ? (
        <WslHint
          wslTokens={wslTokens}
          busy={busy}
          onCreate={() => onCreate("wsl")}
          onRevoke={onRevoke}
        />
      ) : null}
      <p className="c2-card-foot market-install-foot-note">
        所有授权码都会复用「我的账户」里的当前 API Key。撤销并重置 API Key 后，请重新生成授权码与安装命令。
      </p>
    </div>
  );
}

function WslHint({
  wslTokens,
  busy,
  onCreate,
  onRevoke,
}: {
  wslTokens: InstallToken[];
  busy: boolean;
  onCreate: () => void;
  onRevoke: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const usable = wslTokens.find((t) => new Date(t.expires_at).getTime() > Date.now()) || null;
  const cmd = usable?.install_powershell || "";
  return (
    <div className={"market-install-wsl" + (open ? " is-open" : "")}>
      <button
        type="button"
        className="market-install-wsl-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="market-install-wsl-arrow" aria-hidden="true">{open ? "▾" : "▸"}</span>
        Windows 用户：先装 WSL 才能跑上面的 Linux/macOS 命令
      </button>
      {open ? (
        <div className="market-install-wsl-body">
          {usable && cmd ? (
            <>
              <div className="c2-snippet">
                <div className="c2-snippet-label">Windows · PowerShell（管理员）</div>
                <pre className="c2-code"><code>{cmd}</code></pre>
                <CopyIcon
                  className="c2-snippet-copy"
                  text={cmd}
                  ariaLabel="复制 WSL 引导一键安装命令"
                />
              </div>
              <p className="market-install-wsl-meta">
                使用授权码 <code>{usable.token_display_id || usable.token_id}</code>
                ，到期 {formatDateShort(usable.expires_at)}
                <button
                  type="button"
                  className="market-install-wsl-revoke"
                  onClick={() => onRevoke(usable.token_id)}
                >
                  撤销
                </button>
              </p>
            </>
          ) : (
            <>
              <p className="market-install-wsl-note">
                WSL 引导用一个独立的授权码。点下面按钮生成后，复制 PowerShell 命令到管理员终端跑一次即可。
              </p>
              <button
                type="button"
                className="c2-btn c2-btn-primary c2-btn-sm"
                onClick={onCreate}
                disabled={busy}
              >
                {busy ? "生成中…" : "+ 生成 WSL 引导授权码"}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

function InstallTokenCard({
  token,
  onRevoke,
}: {
  token: InstallToken;
  onRevoke: (id: string) => void;
}) {
  const expired = new Date(token.expires_at).getTime() < Date.now();
  const status = token.revoked
    ? { label: "已撤销", cls: "bad" }
    : expired
    ? { label: "已过期", cls: "warn" }
    : { label: "可用", cls: "" };
  const bash = token.install_bash || "";
  const ps = token.install_powershell || "";
  return (
    <li className="market-install-item">
      <header className="market-install-item-head">
        <code className="market-install-item-id">{token.token_display_id || token.token_id}</code>
        <span className={`c2-token-status${status.cls ? " " + status.cls : ""}`}>{status.label}</span>
        <span className="market-install-item-meta">
          到期 {formatDateShort(token.expires_at)} · 用量 {token.use_count || 0}
          {token.max_uses ? `/${token.max_uses}` : ""}
        </span>
        <button
          className="c2-btn c2-btn-ghost c2-btn-sm market-install-item-revoke"
          type="button"
          onClick={() => onRevoke(token.token_id)}
          disabled={token.revoked}
        >
          撤销
        </button>
      </header>
      {bash || ps ? (
        <div className="market-install-cmds">
          {bash ? (
            <div className="c2-snippet">
              <div className="c2-snippet-label">Linux / macOS</div>
              <pre className="c2-code"><code>{bash}</code></pre>
              <CopyIcon
                className="c2-snippet-copy"
                text={bash}
                ariaLabel="复制 Linux/macOS 一键安装命令"
              />
            </div>
          ) : null}
          {ps ? (
            <div className="c2-snippet">
              <div className="c2-snippet-label">Windows</div>
              <pre className="c2-code"><code>{ps}</code></pre>
              <CopyIcon
                className="c2-snippet-copy"
                text={ps}
                ariaLabel="复制 Windows 一键安装命令"
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function formatDateShort(value: string | number | undefined): string {
  if (!value) return "--";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "--";
  const pad = (n: number) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ============================================================
   余额充值 (RechargePanel) — session-aware version
   ============================================================ */

function RechargePanel({ active, session }: PanelChildProps) {
  const [options, setOptions] = useState<RechargeOption[]>([]);
  const [selectedPlanID, setSelectedPlanID] = useState<string>("builder");
  const [order, setOrder] = useState<RechargeOrder | null>(null);
  const [orderMessage, setOrderMessage] = useState<string>("支付通道配置中，生成申请后请联系支持处理。");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchRechargeOptions()
      .then((resp) => {
        if (cancelled) return;
        const opts = resp.options || [];
        setOptions(opts);
        if (opts.length && !opts.find((o) => o.id === "builder")) {
          setSelectedPlanID(opts[0].id);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    if (!session) return;
    const data = new FormData(ev.currentTarget);
    const amountRaw = String(data.get("amount_cny") || "").trim();
    const body = amountRaw ? { amount_cny: Number(amountRaw) } : { plan_id: selectedPlanID };
    setSubmitting(true);
    try {
      const created = await createRechargeOrder(session, body);
      setOrder(created);
      setOrderMessage(created.message || "充值申请已生成");
    } catch (err) {
      setOrderMessage(err instanceof Error ? err.message : "生成充值申请失败");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={cn("c2-panel", active && "active")}>
      <article className="c2-card">
        <header className="c2-card-head">
          <div>
            <span className="c2-eyebrow">套餐</span>
            <h3>选择充值方式</h3>
          </div>
          <span className="c2-card-meta">人工对账，非自动通道</span>
        </header>
        <div className="c2-plans">
          {options.length === 0 ? (
            <button
              className={cn("c2-plan", selectedPlanID === "builder" && "active")}
              type="button"
              data-plan-id="builder"
            >
              <span className="c2-plan-name">开发者按量包</span>
              <strong className="c2-plan-amount">￥200</strong>
              <small className="c2-plan-desc">日常 Codex / Claude Code 使用</small>
            </button>
          ) : (
            options.map((opt) => (
              <button
                key={opt.id}
                className={cn("c2-plan", opt.id === selectedPlanID && "active")}
                type="button"
                data-plan-id={opt.id}
                onClick={() => setSelectedPlanID(opt.id)}
              >
                <span className="c2-plan-name">{opt.name}</span>
                <strong className="c2-plan-amount">￥{formatMoney(opt.amount_cny)}</strong>
                <small className="c2-plan-desc">{opt.description}</small>
              </button>
            ))
          )}
        </div>
      </article>

      <article className="c2-card">
        <header className="c2-card-head">
          <div>
            <span className="c2-eyebrow">自定义</span>
            <h3>按金额提交</h3>
          </div>
        </header>
        <form className="c2-form c2-form-inline" onSubmit={handleSubmit}>
          <label className="c2-field">
            <span>金额（元）</span>
            <input name="amount_cny" type="number" min="1" step="1" placeholder="200" />
          </label>
          <button className="c2-btn c2-btn-primary" type="submit" disabled={submitting || !active}>
            {submitting ? "提交中…" : "生成充值申请"}
          </button>
        </form>
      </article>

      <article className="c2-card c2-order">
        <header className="c2-card-head">
          <div>
            <span className="c2-eyebrow">最近订单</span>
            <h3>充值申请</h3>
          </div>
          <span className="c2-status c2-status-warn">
            {order?.status === "manual_review" ? "人工处理" : order?.status || "待生成"}
          </span>
        </header>
        <dl className="c2-kv">
          <div><dt>订单号</dt><dd>{order?.order_id || "--"}</dd></div>
          <div><dt>充值金额</dt><dd>{order?.amount_cny ? "￥" + formatMoney(order.amount_cny) : "--"}</dd></div>
          <div><dt>到账卡额</dt><dd>{order?.estimated_quota != null ? formatQuota(order.estimated_quota) : "--"}</dd></div>
        </dl>
        <p className="c2-order-note">{orderMessage}</p>
      </article>
    </section>
  );
}

/* ============================================================
   Shared Modal
   ============================================================ */

function Modal({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: React.ReactNode }) {
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("modal-open");
    const onKey = (ev: KeyboardEvent) => ev.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("modal-open");
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="c2-modal" aria-hidden={false}>
      <div className="c2-modal-backdrop" onClick={onClose} />
      <div className="c2-modal-dialog" role="dialog" aria-modal="true">
        <header className="c2-modal-head">
          <h3>{title}</h3>
          <button type="button" className="c2-modal-close" aria-label="关闭" onClick={onClose}>×</button>
        </header>
        {children}
      </div>
    </div>
  );
}
